// Обращения к API. Токен хранится в sessionStorage: при закрытии вкладки
// он пропадает, что для внутренней системы с чувствительными данными
// правильнее долгоживущего хранилища.

const TOKEN_KEY = "waviot_token";
const ROLE_KEY = "waviot_role";
const LOGIN_KEY = "waviot_login";

export type Grade = "very_high" | "high" | "medium" | "low" | "low_prospect" | "";
export type Action = "close" | "merge" | "move_out" | "";

export const GRADE_LABEL: Record<string, string> = {
  very_high: "Очень высокий",
  high: "Высокий",
  medium: "Средний",
  low: "Низкий",
  low_prospect: "Низкая перспектива",
};

// Цвета заливки взяты из эталонного файла — интерфейс должен читаться
// так же, как таблица, к которой люди привыкли.
export const GRADE_COLOR: Record<string, string> = {
  very_high: "#E06666",
  high: "#F4B6B6",
  medium: "#FDEBA8",
  low: "#C6E0B4",
  low_prospect: "#B4C7E7",
};

export const GRADE_TEXT_COLOR: Record<string, string> = {
  very_high: "#FFFFFF",
};

export const ACTION_LABEL: Record<string, string> = {
  close: "ЗАКРЫТЬ",
  merge: "СЛИТЬ",
  move_out: "ВЫНЕСТИ",
};

export const GRADE_ORDER: Grade[] = ["very_high", "high", "medium", "low", "low_prospect"];

export interface Opinion {
  person: string;
  text: string;
}

export interface Proposal {
  grade: Grade;
  action: Action;
  resource: string;
  question: string;
}

export interface Lock {
  login: string;
  expires_at: string;
  mine: boolean;
}

export interface AuditEntry {
  id: number;
  table: string;
  row_id: number | null;
  action: string;
  at: string;
  actor: string;
  ai_assisted: boolean;
  diff: unknown;
}

export interface PortfolioItem {
  id: number;
  src_no: string;
  category: string;
  title: string;
  tasks: string;
  owner_raw: string;
  owners: string[];
  grade: Grade;
  due_text: string;
  status_text: string;
  rationale: string;
  author_note: string;
  opinions: Opinion[];
  proposal: Proposal;
  lock?: Lock;
  comment_count: number;
}

export interface PortfolioComment {
  id: number;
  portfolio_item_id: number;
  author_user_id: number;
  author_login: string;
  body: string;
  created_at: string;
}

export interface PortfolioPresence {
  user_id: number;
  login: string;
  current_item_id?: number;
  expires_at: string;
}

export interface PortfolioBulkPatch {
  id: number;
  grade?: string;
  due_text?: string;
  status_text?: string;
  proposal?: { grade?: string; action?: string };
}

export interface GanttTask {
  id: number;
  ord: number;
  src_no: string;
  block: string;
  title: string;
  description: string;
  assignees: string[];
  note: string;
  priority: "P0" | "P1" | "P2";
  status: string;
  status_label: string;
  start: string;
  due: string;
  fact: string;
  due_revised: string;
  date_note: string;
  is_milestone: boolean;
}

// Цвета статусов и приоритетов — из эталонного план-графика.
export const STATUS_COLOR: Record<string, string> = {
  done: "#2E7D32",
  in_progress: "#1565C0",
  not_started: "#9E9E9E",
  blocked: "#C62828",
  at_risk: "#EF6C00",
};

export const PRIORITY_COLOR: Record<string, string> = {
  P0: "#C62828",
  P1: "#EF6C00",
  P2: "#9E9E9E",
};

export interface PlanTask {
  id: number;
  code: string;
  is_hr: boolean;
  priority: string;
  title: string;
  expectation: string;
  owner: string;
  owner_raw: string;
  due: string;
  due_text: string;
  status: string;
  status_label: string;
  done_at: string;
  next_step: string;
  portfolio_title: string;
  meeting_id?: number;
  meeting_title: string;
  decision_id?: number;
  decision: string;
}

export interface ResourceTask {
  id: number;
  title: string;
  gantt: string;
  priority: string;
  status: string;
  start: string;
  due: string;
  effective_due: string;
  troubled: boolean;
}

export interface MeetingDetail {
  id: number;
  direction: string;
  period: string;
  date: string;
  title: string;
  participants: string;
  body: string;
  confidential: boolean;
  decisions: { id: number; type: string; statement: string; owner: string; owner_raw: string; due: string; status: string }[];
}

export interface ReportTask {
  code: string;
  title: string;
  expectation: string;
  owner: string;
  due: string;
  done_at: string;
  next_step: string;
  status: string;
  days_late?: number;
}

export interface PlanReport {
  plan_name: string;
  as_of: string;
  total: number;
  summary: { status: string; label: string; color: string; count: number }[];
  overdue: ReportTask[];
  awaiting: ReportTask[];
  due_soon: ReportTask[];
  done: ReportTask[];
  in_flight: ReportTask[];
  paused: ReportTask[];
  no_owner: ReportTask[];
  hr_pending: ReportTask[];
}

// Статусы плана-поручения по §3.2 ТЗ.
export const PLAN_STATUS: Record<string, { label: string; color: string }> = {
  not_started: { label: "Не начато", color: "#9E9E9E" },
  in_progress: { label: "В работе", color: "#1565C0" },
  done: { label: "Сделано", color: "#2E7D32" },
  overdue: { label: "Просрочено", color: "#C62828" },
  awaiting: { label: "Ждёт согласования", color: "#EF6C00" },
  paused: { label: "На паузе", color: "#7030A0" },
};

export const PLAN_STATUS_ORDER = [
  "overdue", "awaiting", "in_progress", "not_started", "paused", "done",
];

export interface Finding {
  id: number;
  code: string;
  title: string;
  body: string;
  severity: string;
  item_ids: number[];
}

export interface PortfolioItemDetail extends Omit<PortfolioItem, "opinions" | "lock"> {
  kind: string;
  grade_history: { grade: string; action: string; source: string; comment: string; changed_at: string }[];
  inconsistencies: { id: number; code: string; title: string; body: string; severity: number }[];
}

export interface Material {
  id: number | string;
  kind: string;
  title: string;
  source_file: string;
  records: number;
  changed_at: string;
  route: string;
  origin: "legacy" | "import";
  original_available: boolean;
  status: string;
  version: number | null;
  sha256: string | null;
  byte_size: number | null;
  is_archived: boolean;
}

export class ApiError extends Error {
  status: number;
  constructor(status: number, message: string) {
    super(message);
    this.status = status;
  }
}

export const auth = {
  token: () => sessionStorage.getItem(TOKEN_KEY),
  role: () => sessionStorage.getItem(ROLE_KEY) ?? "",
  login: () => sessionStorage.getItem(LOGIN_KEY) ?? "",
  save(token: string, role: string, login: string) {
    sessionStorage.setItem(TOKEN_KEY, token);
    sessionStorage.setItem(ROLE_KEY, role);
    sessionStorage.setItem(LOGIN_KEY, login);
  },
  clear() {
    sessionStorage.removeItem(TOKEN_KEY);
    sessionStorage.removeItem(ROLE_KEY);
    sessionStorage.removeItem(LOGIN_KEY);
  },
  // Иерархия ролей повторяет серверную: viewer → editor → executive → admin.
  atLeast(required: string): boolean {
    const rank: Record<string, number> = { viewer: 0, editor: 1, executive: 2, admin: 3 };
    return (rank[auth.role()] ?? -1) >= (rank[required] ?? 99);
  },
};

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers = new Headers(init.headers);
  const token = auth.token();
  if (token) headers.set("Authorization", `Bearer ${token}`);
  if (init.body && !(init.body instanceof FormData)) {
    headers.set("Content-Type", "application/json");
  }

  const resp = await fetch(`/api/v1${path}`, { ...init, headers });

  if (resp.status === 204) return undefined as T;

  const text = await resp.text();
  let data: any = null;
  try {
    data = text ? JSON.parse(text) : null;
  } catch {
    data = null;
  }

  if (!resp.ok) {
    // Сервер отвечает по RFC 7807: осмысленное сообщение лежит в detail.
    throw new ApiError(resp.status, data?.detail ?? `Ошибка ${resp.status}`);
  }
  return data as T;
}

export const api = {
  materials: () => request<{ materials: Material[] }>("/materials"),
  archiveMaterial: (id: number) => request<{ id: number; archived: boolean }>(`/imports/${id}/archive`, { method: "PATCH" }),
  materialVersions: (id: number) => request<{ id: number; versions: Material[] }>(`/materials/${id}/versions`),
  async login(login: string, password: string, totp: string) {
    const data = await request<{ token: string; role: string; login: string }>("/auth/login", {
      method: "POST",
      body: JSON.stringify({ login, password, totp_code: totp }),
    });
    auth.save(data.token, data.role, data.login);
    return data;
  },

  portfolio: () => request<{ count: number; items: PortfolioItem[]; presence: PortfolioPresence[] }>("/portfolio"),

  item: (id: number) => request<PortfolioItemDetail>(`/portfolio/${id}`),
  itemComments: (id: number) => request<{ comments: PortfolioComment[] }>(`/portfolio/${id}/comments`),
  addItemComment: (id: number, body: string) => request<PortfolioComment>(`/portfolio/${id}/comments`, { method: "POST", body: JSON.stringify({ body }) }),
  presence: (currentItemID?: number) => request<void>("/portfolio/presence", { method: "POST", body: JSON.stringify(currentItemID ? { current_item_id: currentItemID } : {}) }),
  clearPortfolioPresence: () => request<void>("/portfolio/presence", { method: "DELETE" }),

  lock: (id: number) => request<{ expires_at: string }>(`/portfolio/${id}/lock`, { method: "POST" }),
  unlock: (id: number) => request<void>(`/portfolio/${id}/lock`, { method: "DELETE" }),

  patchItem: (id: number, body: Record<string, unknown>) =>
    request<{ id: number }>(`/portfolio/${id}`, { method: "PATCH", body: JSON.stringify(body) }),

  bulkPortfolio: (items: PortfolioBulkPatch[]) =>
    request<{ updated: number }>("/portfolio/bulk", { method: "POST", body: JSON.stringify({ items }) }),

  previewPortfolioPaste: (tsv: string, targetIDs: number[], startField: string) =>
    request<{ items: PortfolioBulkPatch[]; count: number }>("/portfolio/paste/preview", {
      method: "POST", body: JSON.stringify({ tsv, target_ids: targetIDs, start_field: startField }),
    }),

  inconsistencies: () =>
    request<{
      count: number;
      summary: { code: string; title: string; severity: string; count: number }[];
      findings: Finding[];
    }>("/inconsistencies"),

  rescan: () => request<{ findings: number }>("/inconsistencies/rescan", { method: "POST" }),
  resolveFinding: (id: number, reason: string) =>
    request<{ id: number }>(`/inconsistencies/${id}/resolve`, {
      method: "POST",
      body: JSON.stringify({ reason }),
    }),

  snapshots: () =>
    request<{ snapshots: { id: number; taken_at: string; note: string; items: number }[] }>("/snapshots"),

  createSnapshot: (note: string, takenAt?: string) =>
    request<{ id: number; taken_at: string }>("/snapshots", {
      method: "POST",
      body: JSON.stringify({ note, taken_at: takenAt ?? "" }),
    }),

  diff: (from: number, to: number) =>
    request<{
      summary: { added: number; removed: number; changed: number; unchanged: number };
      diff: {
        added: { title: string }[];
        removed: { title: string }[];
        changed: { title: string; changes: { field: string; from: string; to: string }[] }[];
      };
    }>(`/snapshots/diff?from=${from}&to=${to}`),

  resources: () =>
    request<{
      threshold: number;
      people: {
        person_id: number;
        name: string;
        open_tasks: number;
        open_p0: number;
        open_critical: number;
        troubled: number;
        gantts: number;
        gantt_names: string[];
        bottleneck: boolean;
      }[];
    }>("/resources/load"),

  resourceTasks: (personID: number) =>
    request<{ person_id: number; tasks: ResourceTask[] }>(`/resources/${personID}/tasks`),

  previewPortfolioImport(file: File) {
    const form = new FormData();
    form.append("file", file);
    return request<{
      filename: string;
      summary: { added: number; removed: number; changed: number; unchanged: number; sample: { kind: string; title: string; fields?: string[] }[] };
      errors: string[];
    }>("/imports/portfolio/preview", { method: "POST", body: form });
  },
  importPortfolio(file: File, replace = false) {
    const form = new FormData();
    form.append("file", file);
    form.append("replace", String(replace));
    return request<{ stats: Record<string, number>; errors: string[] }>("/imports/portfolio", {
      method: "POST",
      body: form,
    });
  },
  importSnapshot(file: File, takenAt: string, note = "") {
    const form = new FormData(); form.append("file", file); form.append("taken_at", takenAt); form.append("note", note);
    return request<{ id: number; items: number }>("/imports/snapshot", { method: "POST", body: form });
  },

  exportPortfolioURL: () => "/api/v1/portfolio/export.xlsx",

  users: () =>
    request<{
      users: { id: number; login: string; role: string; active: boolean;
               has_totp: boolean; person: string; created_at: string }[];
    }>("/admin/users"),

  createUser: (login: string, role: string, withTOTP: boolean) =>
    request<{ id: number; login: string; password: string; otpauth: string; note: string }>(
      "/admin/users",
      { method: "POST", body: JSON.stringify({ login, role, with_totp: withTOTP }) },
    ),

  updateUser: (id: number, body: Record<string, unknown>) =>
    request<{ id: number }>(`/admin/users/${id}`, { method: "PATCH", body: JSON.stringify(body) }),

  resetPassword: (id: number) =>
    request<{ id: number; password: string; note: string }>(`/admin/users/${id}/password`, {
      method: "POST",
    }),

  dashboard: () => request<Record<string, any>>("/dashboard"),

  audit: (filters: { entity?: string; actor?: string; action?: string; ai_assisted?: boolean; from?: string; to?: string; page?: number; limit?: number } | number = {}) => {
    const values = typeof filters === "number" ? { limit: filters } : filters;
    const query = new URLSearchParams(Object.entries(values).filter(([, value]) => value !== undefined && value !== "").map(([key, value]) => [key, String(value)]));
    return request<{ count: number; total: number; page: number; limit: number; entries: AuditEntry[] }>(`/audit?${query}`);
  },

  plans: () =>
    request<{ plans: { id: number; name: string; tasks: number; overdue: number; created_at: string }[] }>(
      "/plans",
    ),

  planTasks: (id: number) => request<{ tasks: PlanTask[] }>(`/plans/${id}/tasks`),

  planReport: (id: number, asOf: string) =>
    request<PlanReport>(`/plans/${id}/report?as_of=${asOf}`),

  planReportTextURL: (id: number, asOf: string) =>
    `/api/v1/plans/${id}/report?as_of=${asOf}&format=text`,

  patchPlanTask: (id: number, body: Record<string, unknown>) =>
    request<{ id: number }>(`/plan-tasks/${id}`, { method: "PATCH", body: JSON.stringify(body) }),

  bulkUpdatePlanTasks: (planID: number, body: { task_ids: number[]; status?: string; next_step?: string }) =>
    request<{ updated: number }>(`/plans/${planID}/tasks/bulk`, { method: "POST", body: JSON.stringify(body) }),

  importPlan(file: File, name: string) {
    const form = new FormData();
    form.append("file", file);
    form.append("name", name);
    return request<{ stats: { tasks: number; hr_tasks: number; overdue: number } }>("/imports/plan", {
      method: "POST",
      body: form,
    });
  },

  gantts: () =>
    request<{ gantts: { id: number; name: string; period_start: string; period_end: string; tasks: number }[] }>(
      "/gantts",
    ),

  gantt: (id: number) =>
    request<{
      id: number;
      name: string;
      subtitle: string;
      blocks: { ord: number; name: string; color: string }[];
      tasks: GanttTask[];
      risks: { kind: string; title: string; body: string }[];
      summary: Record<string, number>;
    }>(`/gantts/${id}`),

  patchGanttTask: (ganttID: number, taskID: number, body: Record<string, unknown>) =>
    request<{ id: number }>(`/gantts/${ganttID}/tasks/${taskID}`, {
      method: "PATCH", body: JSON.stringify(body),
    }),

  exportGanttURL: (id: number) => `/api/v1/gantts/${id}/export.xlsx`,

  importGantt(file: File) {
    const form = new FormData();
    form.append("file", file);
    return request<{ stats: Record<string, unknown>; summary: Record<string, number> }>(
      "/imports/gantt",
      { method: "POST", body: form },
    );
  },

  meetings: () =>
    request<{
      total: number;
      confidential: number;
      meetings: {
        id: number; direction: string; period: string; date: string;
        title: string; participants: string; confidential: boolean; body_length: number;
      }[];
    }>("/meetings"),

  searchMeetings: (q: string) =>
    request<{
      count: number;
      results: { id: number; direction: string; date: string; title: string; confidential: boolean; excerpt: string }[];
    }>(`/meetings/search?q=${encodeURIComponent(q)}`),

  meeting: (id: number) => request<MeetingDetail>(`/meetings/${id}`),

  createPlanTaskFromSource: (body: { plan_id: number; meeting_id: number; decision_id?: number; title: string }) =>
    request<{ id: number; plan_id: number }>("/plan-tasks/from-source", {
      method: "POST", body: JSON.stringify(body),
    }),

  setConfidential: (id: number, confidential: boolean) =>
    request<{ id: number }>(`/meetings/${id}/confidential`, {
      method: "PATCH",
      body: JSON.stringify({ confidential }),
    }),

  bulkConfidential: (direction: string, period: string, confidential: boolean) =>
    request<{ updated: number }>("/meetings/confidential/bulk", {
      method: "POST",
      body: JSON.stringify({ direction, period, confidential }),
    }),

  importMeetings(file: File) {
    const form = new FormData();
    form.append("file", file);
    return request<{ stats: Record<string, number>; problems: { path: string; reason: string }[] }>(
      "/imports/meetings",
      { method: "POST", body: form },
    );
  },

  previewPrompt: (q: string) =>
    request<{
      text: string; meeting_ids: number[]; masked_people: number[];
      chunk_count: number; retriever: string;
    }>(`/ai/preview?q=${encodeURIComponent(q)}`),

  aiStatus: () => request<{ provider: string; model: string; is_external: boolean; enabled: boolean }>("/ai/status"),

  ask: (question: string) =>
    request<{
      text: string; call_id: number; provider: string; model: string;
      is_external: boolean; meeting_ids: number[]; masked_people: number[]; chunk_count: number;
      citations: { meeting_id: number; title: string; date: string; direction: string }[];
    }>("/ai/ask", { method: "POST", body: JSON.stringify({ question }) }),

  createReviewedAIPlanTask: (body: { call_id: number; plan_id: number; meeting_id: number; title: string; owner_id?: number; due?: string; priority: string }) =>
    request<{ id: number; plan_id: number }>("/ai/actions/plan-task", { method: "POST", body: JSON.stringify(body) }),

  reindex: () => request<{ meetings: number; chunks: number }>("/ai/reindex", { method: "POST" }),

  llmCalls: () =>
    request<{
      count: number;
      calls: {
        id: number; provider: string; model: string; external: boolean; purpose: string;
        masked_people: number[]; meeting_ids: number[]; response_chars: number;
        created_at: string; login: string;
      }[];
    }>("/ai/calls"),

  llmCallPayload: (id: number) => request<{ id: number; created_at: string; sent: unknown }>(`/ai/calls/${id}/payload`),
};
